import React, { useState, useMemo } from 'react';
import {
  Briefcase,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  Phone,
  Plus,
  Send,
  Trash2,
  User,
  X,
  Zap,
} from 'lucide-react';

const SENTIMENT_STYLES = {
  Warm:    { bg: 'bg-emerald-500/10', text: 'text-emerald-300', border: 'border-emerald-400/20' },
  Neutral: { bg: 'bg-slate-500/10',  text: 'text-slate-400',   border: 'border-slate-500/20' },
  Passive: { bg: 'bg-sky-500/10',    text: 'text-sky-300',     border: 'border-sky-400/20' },
  Ghosted: { bg: 'bg-amber-500/10',  text: 'text-amber-300',   border: 'border-amber-400/20' },
  Cold:    { bg: 'bg-rose-500/10',   text: 'text-rose-300',    border: 'border-rose-400/20' },
};

const CHANNEL_CFG = {
  email:   { icon: Mail,     color: 'text-cyan-400',    border: 'border-cyan-400/25',    bg: 'bg-cyan-500/10' },
  call:    { icon: Phone,    color: 'text-emerald-400', border: 'border-emerald-400/25', bg: 'bg-emerald-500/10' },
  meeting: { icon: Calendar, color: 'text-violet-400',  border: 'border-violet-400/25',  bg: 'bg-violet-500/10' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function fmtMoney(value, currency = 'ZAR') {
  const symbols = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const amount = Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return `${symbols[currency] || currency || 'R'} ${amount}`;
}

function fmtCurrencyMap(map = {}) {
  const entries = Object.entries(map).filter(([, value]) => Number(value || 0) > 0);
  if (!entries.length) return fmtMoney(0, 'ZAR');
  return entries.map(([currency, value]) => fmtMoney(value, currency)).join(' · ');
}

function getEmailTemplate(stepLabel, contactName, companyName) {
  const first = (contactName || '').split(' ')[0] || 'there';
  const co = companyName || 'your company';
  const label = (stepLabel || '').toLowerCase();
  if (/intro|pitch|who we are/.test(label))
    return {
      subject: `Introducing Vision Team — recruitment for ${co}`,
      body: `Hi ${first},\n\nI wanted to reach out and introduce myself — I'm a specialist recruiter and I work with a number of companies in your space.\n\nI'd love to explore whether we could support any current or upcoming hiring needs at ${co}.\n\nWould you have 15 minutes this week?\n\nBest regards`,
    };
  if (/case study|placement|share/.test(label))
    return {
      subject: `Relevant hire we recently made — thought you'd find it interesting`,
      body: `Hi ${first},\n\nFollowing our recent conversation, I wanted to share a relevant hire we completed that may resonate with your situation at ${co}.\n\n[Insert placement details here]\n\nI'd be happy to discuss how we could replicate this for you.\n\nBest regards`,
    };
  if (/check.in|follow.up|touch base/.test(label))
    return {
      subject: `Quick follow-up — ${co}`,
      body: `Hi ${first},\n\nJust checking in to see how things are going at ${co} and whether there are any updates on the hiring front.\n\nLet me know if there's a good time to connect.\n\nBest regards`,
    };
  if (/discovery|call attempt/.test(label))
    return {
      subject: `Quick call — ${co}?`,
      body: `Hi ${first},\n\nI tried to reach you by phone — would love to set up a quick call to understand your current hiring challenges at ${co}.\n\nAre you free for 15 minutes sometime this week?\n\nBest regards`,
    };
  if (/market update|miss you|re.engag/.test(label))
    return {
      subject: `${co} — a few things have changed in the market`,
      body: `Hi ${first},\n\nIt's been a while since we last spoke and I wanted to reconnect. The talent market has shifted and I have some insights that may be relevant to ${co}.\n\nWould love to catch up — do you have 20 minutes?\n\nBest regards`,
    };
  if (/breakup|last touch/.test(label))
    return {
      subject: `Last reach out — ${co}`,
      body: `Hi ${first},\n\nI've tried to connect a few times without success — I completely understand if the timing isn't right.\n\nI'll leave the door open. Please don't hesitate to reach out if you ever need recruitment support.\n\nAll the best`,
    };
  if (/satisfaction|30.day|check.in/.test(label))
    return {
      subject: `30-day check-in — how is the new hire settling in?`,
      body: `Hi ${first},\n\nI wanted to check in now that the new hire has been with ${co} for a month. Hoping they've settled in well!\n\nAny feedback on their performance or fit would be really valuable.\n\nBest regards`,
    };
  return {
    subject: `Following up — ${co}`,
    body: `Hi ${first},\n\nI wanted to follow up on our recent conversation regarding ${co}.\n\nBest regards`,
  };
}

function getCallNoteTemplate(stepLabel, companyName) {
  return `${stepLabel || 'Call'} — ${companyName || ''}\n\nKey points discussed:\n- \n\nNext steps:\n- `;
}

export default function ContactPanel({
  contact,
  touchpoints = [],         // clientCrmTouchpoints filtered by companyName
  opportunities = [],       // clientCrmOpportunities filtered by contactEmail or companyName
  jobs = [],                // jobs related to this company
  commercialRollup = null,  // commercial object connecting deals, jobs, submissions, placements, revenue
  activityLog = [],         // raw activity log to derive history
  cadenceEnrollment = null, // active crmCadenceEnrollment for this contact
  cadenceTemplates = [],    // BUILTIN_CADENCES + user cadences
  onLogEmail,
  onLogCall,
  onLogMeeting,
  onLogWithNote,            // optional: (contact, type, note) — enhanced log with body
  onSendCadenceEmail,       // optional: (contact, { subject, body, stepIndex, enrollmentId }) => Promise<token>
  onMarkReplied,            // optional: (trackingToken) => void
  emailTracking = [],       // crmEmailTracking records for this contact
  smtpConfigured = false,   // whether SMTP is configured
  onSaveCustomField,        // (contactId, key, value)
  onDeleteCustomField,      // (contactId, key)
  onEnrollCadence,          // (contactId, cadenceId)
  onAdvanceCadence,         // (enrollmentId)
  onPauseCadence,           // (enrollmentId)
  onUpdateContact,          // (contactId, fields)
  onClose,
}) {
  const [tab, setTab] = useState('overview');
  const [cfKey, setCfKey] = useState('');
  const [cfVal, setCfVal] = useState('');
  const [cfEditKey, setCfEditKey] = useState(null);
  const [cfEditVal, setCfEditVal] = useState('');
  const [noteDraft, setNoteDraft] = useState(contact?.notes || '');
  const [noteSaving, setNoteSaving] = useState(false);
  const [sentimentEdit, setSentimentEdit] = useState(false);
  const [stepCompose, setStepCompose] = useState(null); // { channel, subject, body } or null
  const [stepLogging, setStepLogging] = useState(false);

  const openStepCompose = (step) => {
    if (!step) return;
    if (step.channel === 'email') {
      const tpl = getEmailTemplate(step.label, contact?.name, contact?.companyName || contact?.company);
      setStepCompose({ channel: 'email', subject: tpl.subject, body: tpl.body });
    } else {
      setStepCompose({ channel: step.channel, body: getCallNoteTemplate(step.label, contact?.companyName || contact?.company) });
    }
  };

  const handleLogAndAdvance = async () => {
    if (!stepCompose || !cadenceEnrollment) return;
    setStepLogging(true);
    try {
      if (stepCompose.channel === 'email' && onSendCadenceEmail) {
        // Real send via Cloud Function — tracks opens/clicks/replies automatically
        await onSendCadenceEmail(contact, {
          subject: stepCompose.subject,
          body: stepCompose.body,
          stepIndex: cadenceEnrollment.currentStep,
          enrollmentId: cadenceEnrollment.id,
        });
      } else {
        const noteText = stepCompose.channel === 'email'
          ? `Subject: ${stepCompose.subject}\n\n${stepCompose.body}`
          : stepCompose.body;
        if (onLogWithNote) {
          await onLogWithNote(contact, stepCompose.channel, noteText);
        } else if (stepCompose.channel === 'email') {
          await onLogEmail?.(contact);
        } else if (stepCompose.channel === 'call') {
          await onLogCall?.(contact);
        } else {
          await onLogMeeting?.(contact);
        }
      }
      await onAdvanceCadence?.(cadenceEnrollment.id);
      setStepCompose(null);
    } finally {
      setStepLogging(false);
    }
  };

  const sentStyle = SENTIMENT_STYLES[contact?.sentiment] || SENTIMENT_STYLES.Neutral;
  const customFields = Array.isArray(contact?.customFields) ? contact.customFields : [];
  const commercial = commercialRollup || {};
  const commercialHealthStyle = commercial.health === 'Revenue risk' || commercial.health === 'At risk'
    ? 'border-rose-400/25 bg-rose-500/10 text-rose-300'
    : commercial.health === 'Needs feedback'
      ? 'border-amber-400/25 bg-amber-500/10 text-amber-300'
      : commercial.health === 'Active'
        ? 'border-cyan-400/25 bg-cyan-500/10 text-cyan-300'
        : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-300';
  const commercialTimeline = Array.isArray(commercial.timeline) ? commercial.timeline : [];
  const commercialLabels = Array.isArray(commercial.labels) ? commercial.labels : [];

  // ── Full history: merge touchpoints + activity log entries for this contact ──
  const history = useMemo(() => {
    const items = [];
    const contactEmail = String(contact?.email || '').toLowerCase();
    const contactName = String(contact?.name || '').toLowerCase();
    const companyKey = String(contact?.companyName || contact?.company || '').toLowerCase();

    touchpoints.forEach((tp) => {
      items.push({
        id: tp.id,
        type: tp.type || 'outreach',
        summary: tp.summary,
        iso: tp.createdAtIso,
        channel: /email/i.test(tp.type) ? 'email' : /call/i.test(tp.type) ? 'call' : /meeting/i.test(tp.type) ? 'meeting' : 'general',
      });
    });

    activityLog.forEach((a) => {
      const meta = a.metadata || {};
      const isRelevant =
        (meta.contactEmail && meta.contactEmail.toLowerCase() === contactEmail) ||
        (meta.contact && String(meta.contact).toLowerCase() === contactName) ||
        (contactEmail && String(a.message || '').toLowerCase().includes(contactEmail)) ||
        (companyKey && (meta.company || meta.clientCompany || '').toLowerCase().includes(companyKey));
      if (!isRelevant) return;
      if (items.find((i) => i.id === a.id)) return; // dedup with touchpoints
      items.push({
        id: a.id,
        type: a.type || 'activity',
        summary: a.message || a.type,
        iso: a.createdAtIso || a.createdAt,
        channel: /email/i.test(a.type) ? 'email' : /call/i.test(a.type) ? 'call' : /meeting/i.test(a.type) ? 'meeting' : 'general',
      });
    });

    return items.sort((a, b) => new Date(b.iso || 0) - new Date(a.iso || 0));
  }, [touchpoints, activityLog, contact]);

  // ── Cadence progress ─────────────────────────────────────────────────────
  const activeStep = cadenceEnrollment
    ? (cadenceTemplates.find((c) => c.id === cadenceEnrollment.cadenceId)?.steps || [])[cadenceEnrollment.currentStep || 0]
    : null;
  const totalSteps = cadenceEnrollment
    ? (cadenceTemplates.find((c) => c.id === cadenceEnrollment.cadenceId)?.steps || []).length
    : 0;

  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'commercial', label: `Commercial${commercialTimeline.length ? ` · ${commercialTimeline.length}` : ''}` },
    { id: 'history',   label: `History${history.length ? ` (${history.length})` : ''}` },
    { id: 'fields',    label: `Custom fields${customFields.length ? ` · ${customFields.length}` : ''}` },
    { id: 'cadence',   label: cadenceEnrollment ? `Cadence · Step ${(cadenceEnrollment.currentStep || 0) + 1}/${totalSteps}` : 'Cadence' },
    { id: 'linked',    label: `Linked${(opportunities.length + jobs.length) ? ` · ${opportunities.length + jobs.length}` : ''}` },
  ];

  const initials = (contact?.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-slate-950">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-meta font-black uppercase tracking-[0.14em] text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <X size={13} /> Close
          </button>
          <div className="hidden text-xs text-slate-500 sm:block">
            Contact 360 → {contact?.name || contact?.email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onLogEmail && onLogEmail(contact)} className="rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-300 hover:text-white flex items-center gap-1.5"><Mail size={11} /> Log email</button>
          <button onClick={() => onLogCall && onLogCall(contact)} className="rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-300 hover:text-white flex items-center gap-1.5"><Phone size={11} /> Log call</button>
          <button onClick={() => onLogMeeting && onLogMeeting(contact)} className="rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-300 hover:bg-cyan-500/20 flex items-center gap-1.5"><Calendar size={11} /> Log meeting</button>
        </div>
      </div>

      {/* Hero */}
      <div className="shrink-0 border-b border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 px-8 py-5">
        <div className="mx-auto flex max-w-4xl items-start gap-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-card border border-white/10 bg-slate-800 text-base font-black text-white">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black text-white">{contact?.name || '—'}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
              {contact?.role && <span>{contact.role}</span>}
              {(contact?.companyName || contact?.company) && (
                <>
                  {contact?.role && <span className="text-slate-600">·</span>}
                  <span className="flex items-center gap-1"><Briefcase size={11} /> {contact.companyName || contact.company}</span>
                </>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {contact?.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-xs text-cyan-400 hover:underline"><Mail size={11} />{contact.email}</a>
              )}
              {contact?.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-xs text-emerald-400 hover:underline"><Phone size={11} />{contact.phone}</a>
              )}
              {contact?.linkedIn && (
                <a href={contact.linkedIn} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-sky-400 hover:underline"><ExternalLink size={11} />LinkedIn</a>
              )}
            </div>
            {commercialRollup && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${commercialHealthStyle}`}>{commercial.health || 'Healthy'}</span>
                <span className="rounded-full border border-white/10 bg-slate-950/60 px-2.5 py-1 text-micro font-bold text-slate-300">{commercial.submissions?.length || 0} submissions</span>
                <span className="rounded-full border border-white/10 bg-slate-950/60 px-2.5 py-1 text-micro font-bold text-slate-300">{commercial.openOpportunities?.length || 0} open deals</span>
                {(commercial.totalRevenue || 0) > 0 && <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-micro font-bold text-emerald-300">{fmtCurrencyMap(commercial.revenueByCurrency)}</span>}
                {commercial.nextAction && <button type="button" onClick={() => setTab('commercial')} className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1 text-micro font-black text-cyan-300 transition hover:bg-cyan-500/20">{commercial.nextAction}</button>}
              </div>
            )}
          </div>
          {/* Sentiment badge — clickable */}
          <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
            {sentimentEdit ? (
              <div className="flex flex-col gap-1">
                {Object.keys(SENTIMENT_STYLES).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { onUpdateContact && onUpdateContact(contact.id, { sentiment: s }); setSentimentEdit(false); }}
                    className={`rounded-full border px-3 py-1 text-micro font-black text-left ${SENTIMENT_STYLES[s].border} ${SENTIMENT_STYLES[s].bg} ${SENTIMENT_STYLES[s].text}`}
                  >{s}</button>
                ))}
                <button type="button" onClick={() => setSentimentEdit(false)} className="text-micro text-slate-600 hover:text-slate-400 text-center mt-1">Cancel</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSentimentEdit(true)}
                className={`rounded-full border px-3 py-1.5 text-micro font-black ${sentStyle.border} ${sentStyle.bg} ${sentStyle.text}`}
              >
                {contact?.sentiment || 'Neutral'}
              </button>
            )}
            <div className="text-micro text-slate-600">Last touch {fmtRelative(contact?.lastInteractionAtIso || contact?.updatedAtIso)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 border-b border-white/10 px-8">
        <div className="mx-auto flex max-w-4xl overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 border-b-2 px-5 py-3.5 text-meta font-black uppercase tracking-[0.14em] transition ${
                tab === t.id ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-4xl">

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
              <div className="space-y-5">
                {commercialRollup && (
                  <div className="rounded-card border border-cyan-400/15 bg-cyan-500/[0.04] p-5">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Commercial control point</div>
                        <div className="mt-1 text-sm text-slate-300">{commercial.nextAction || 'Maintain relationship'}</div>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${commercialHealthStyle}`}>{commercial.health || 'Healthy'}</span>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-4">
                      {[
                        ['Revenue', fmtCurrencyMap(commercial.revenueByCurrency)],
                        ['Weighted pipe', fmtMoney(commercial.openWeightedPipeline || 0, 'ZAR')],
                        ['Submissions', commercial.submissions?.length || 0],
                        ['Unpaid invoices', commercial.unpaidInvoices?.length || 0],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-ctl border border-white/10 bg-slate-950/55 px-3 py-2">
                          <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
                          <div className="mt-1 text-sm font-black text-white">{value}</div>
                        </div>
                      ))}
                    </div>
                    {commercialLabels.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {commercialLabels.map((label) => (
                          <span key={label} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-bold text-slate-300">{label}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Bio / contact info */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Contact info</div>
                  <dl className="space-y-3 text-sm">
                    {[
                      ['Name', contact?.name],
                      ['Role', contact?.role],
                      ['Company', contact?.companyName || contact?.company],
                      ['Email', contact?.email ? <a href={`mailto:${contact.email}`} className="text-cyan-400 hover:underline">{contact.email}</a> : null],
                      ['Phone', contact?.phone ? <a href={`tel:${contact.phone}`} className="text-emerald-400 hover:underline">{contact.phone}</a> : null],
                      ['LinkedIn', contact?.linkedIn ? <a href={contact.linkedIn} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={11} />View profile</a> : null],
                      ['Source', contact?.source?.replace(/_/g, ' ')],
                    ].filter(([, v]) => v).map(([label, val]) => (
                      <div key={label} className="flex items-start gap-4">
                        <dt className="w-20 shrink-0 text-slate-500">{label}</dt>
                        <dd className="flex-1 font-semibold text-slate-200">{val}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Notes */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Notes</div>
                  <textarea
                    rows={4}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add notes about this contact…"
                    className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-400"
                  />
                  <button
                    type="button"
                    disabled={noteSaving || noteDraft === (contact?.notes || '')}
                    onClick={async () => { setNoteSaving(true); await onUpdateContact?.(contact.id, { notes: noteDraft }); setNoteSaving(false); }}
                    className="mt-2 rounded-full bg-cyan-400 px-4 py-1.5 text-micro font-black text-slate-950 transition hover:bg-cyan-300 disabled:opacity-40"
                  >
                    {noteSaving ? 'Saving…' : 'Save notes'}
                  </button>
                </div>

                {/* Recent activity */}
                {history.length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Recent activity</div>
                    <div className="space-y-2">
                      {history.slice(0, 4).map((item) => {
                        const chCfg = CHANNEL_CFG[item.channel] || CHANNEL_CFG.email;
                        const Icon = chCfg.icon;
                        return (
                          <div key={item.id} className="flex items-start gap-3">
                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${chCfg.border} ${chCfg.bg}`}>
                              <Icon size={9} className={chCfg.color} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-300 leading-5 truncate">{item.summary}</p>
                            </div>
                            <div className="shrink-0 text-micro text-slate-600">{fmtRelative(item.iso)}</div>
                          </div>
                        );
                      })}
                    </div>
                    {history.length > 4 && (
                      <button type="button" onClick={() => setTab('history')} className="mt-3 text-xs text-cyan-400 hover:underline">
                        View all {history.length} events →
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Active cadence */}
                {cadenceEnrollment && activeStep && (
                  <div className="rounded-card border border-violet-400/25 bg-violet-500/8 p-4">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-violet-400">Active cadence</div>
                    <div className="text-xs font-bold text-white mb-1">{cadenceEnrollment.cadenceName}</div>
                    <div className="text-micro text-slate-500 mb-3">Step {(cadenceEnrollment.currentStep || 0) + 1} of {totalSteps}</div>
                    <div className="rounded-ctl border border-violet-400/15 bg-violet-500/5 px-3 py-2.5 mb-3">
                      <div className="text-micro font-black uppercase tracking-[0.12em] text-violet-400 mb-1">Next action</div>
                      <div className="text-xs text-slate-200">{activeStep.label}</div>
                      <div className="mt-1 text-micro text-slate-500">Channel: {activeStep.channel}</div>
                    </div>
                    {cadenceEnrollment.nextStepDueIso && (
                      <div className="text-micro text-slate-500 mb-3">Due {fmtDate(cadenceEnrollment.nextStepDueIso)}</div>
                    )}
                    {stepCompose ? (
                      <div className="mt-2 space-y-2">
                        {stepCompose.channel === 'email' && (
                          <input
                            value={stepCompose.subject}
                            onChange={(e) => setStepCompose((s) => ({ ...s, subject: e.target.value }))}
                            className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-meta text-white outline-none focus:border-violet-400"
                            placeholder="Subject…"
                          />
                        )}
                        <textarea
                          rows={5}
                          value={stepCompose.body}
                          onChange={(e) => setStepCompose((s) => ({ ...s, body: e.target.value }))}
                          className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-meta text-slate-200 outline-none focus:border-violet-400 font-mono leading-5"
                        />
                        <div className="flex gap-2">
                          <button type="button" disabled={stepLogging} onClick={handleLogAndAdvance} className="flex-1 rounded-full bg-violet-500 py-1.5 text-micro font-black text-white hover:bg-violet-400 disabled:opacity-40 transition">
                            {stepLogging ? 'Sending…' : stepCompose.channel === 'email' && onSendCadenceEmail ? 'Send & advance →' : `Log ${stepCompose.channel} & advance →`}
                          </button>
                          <button type="button" onClick={() => { onAdvanceCadence?.(cadenceEnrollment.id); setStepCompose(null); }} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro text-slate-400 hover:text-white transition">Skip</button>
                          <button type="button" onClick={() => setStepCompose(null)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro text-slate-500 hover:text-white transition">✕</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openStepCompose(activeStep)} className="flex-1 rounded-full bg-violet-500/20 border border-violet-400/30 py-1.5 text-micro font-black text-violet-300 hover:bg-violet-500/30 transition">Mark done →</button>
                        <button type="button" onClick={() => onPauseCadence?.(cadenceEnrollment.id)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro text-slate-400 hover:text-white transition">Pause</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick stats */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Quick stats</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Interactions</span><span className="font-black text-white">{history.length}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Open opps</span><span className="font-black text-white">{opportunities.filter(o => !['Closed-Won','Closed-Lost'].includes(o.stage)).length}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Active roles</span><span className="font-black text-white">{jobs.filter(j => (j.status || '').toLowerCase() === 'active').length}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Added</span><span className="font-semibold text-slate-300">{fmtDate(contact?.createdAtIso || contact?.updatedAtIso)}</span></div>
                  </div>
                </div>

                {/* Tags */}
                {(Array.isArray(contact?.tags) && contact.tags.length > 0) && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Tags</div>
                    <div className="flex flex-wrap gap-1.5">
                      {contact.tags.map((t) => (
                        <span key={t} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2 py-0.5 text-micro font-semibold text-cyan-300">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── HISTORY ── */}
          {tab === 'history' && (
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-500">{history.length} interaction events</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => onLogEmail?.(contact)} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black text-slate-300 hover:text-white transition"><Mail size={10} /> Log email</button>
                  <button type="button" onClick={() => onLogCall?.(contact)} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black text-slate-300 hover:text-white transition"><Phone size={10} /> Log call</button>
                  <button type="button" onClick={() => onLogMeeting?.(contact)} className="flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-micro font-black text-cyan-300 hover:bg-cyan-500/20 transition"><Calendar size={10} /> Meeting</button>
                </div>
              </div>

              {/* ── Sent email tracking ── */}
              {emailTracking.length > 0 && (
                <div className="mb-5 rounded-card border border-violet-500/20 bg-violet-500/5 p-4">
                  <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-violet-400">Sent emails ({emailTracking.length})</div>
                  <div className="space-y-2">
                    {emailTracking.slice(0, 10).map((t) => (
                      <div key={t.id} className="flex items-start justify-between gap-3 rounded-ctl bg-slate-900/60 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-meta font-semibold text-slate-200">{t.subject || '(no subject)'}</div>
                          <div className="mt-0.5 text-micro text-slate-500">{t.sentAtIso ? new Date(t.sentAtIso).toLocaleString() : '—'}</div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-1">
                          <span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-micro font-bold text-cyan-300">Sent</span>
                          {t.opened && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-micro font-bold text-emerald-300">Opened{t.openCount > 1 ? ` ×${t.openCount}` : ''}</span>}
                          {t.clicks?.length > 0 && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-micro font-bold text-violet-300">Clicked</span>}
                          {t.replied ? (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-micro font-bold text-amber-300">Replied</span>
                          ) : (
                            <button type="button" onClick={() => onMarkReplied?.(t.id)} className="rounded-full border border-amber-400/20 bg-amber-500/5 px-2 py-0.5 text-micro font-bold text-amber-400 hover:bg-amber-500/15 transition">Mark replied</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {history.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Send size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No interaction history yet.</div>
                  <div className="mt-1 text-xs text-slate-600">Log an email, call, or meeting to start building the history.</div>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-4 left-[19px] top-4 w-px bg-white/10" />
                  {history.map((item) => {
                    const chCfg = CHANNEL_CFG[item.channel] || { icon: Zap, color: 'text-slate-400', border: 'border-white/15', bg: 'bg-white/5' };
                    const Icon = chCfg.icon;
                    return (
                      <div key={item.id} className="relative flex gap-4 py-2">
                        <div className={`relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${chCfg.border} ${chCfg.bg}`}>
                          <Icon size={9} className={chCfg.color} />
                        </div>
                        <div className="flex-1 rounded-card border border-white/[0.07] bg-slate-900/50 px-4 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-slate-200 leading-5">{item.summary}</p>
                            <div className="shrink-0 text-micro text-slate-600">{fmtDate(item.iso)}</div>
                          </div>
                          <div className="mt-1 inline-block rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro text-slate-500">
                            {item.type.replace(/_/g, ' ')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── CUSTOM FIELDS ── */}
          {tab === 'fields' && (
            <div className="max-w-xl space-y-5">
              <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Custom fields — store anything about this contact</div>
                {customFields.length === 0 ? (
                  <div className="mb-4 text-sm text-slate-500">No custom fields yet. Add any key/value pair below.</div>
                ) : (
                  <div className="mb-4 space-y-2">
                    {customFields.map(({ key, value }) => (
                      <div key={key} className="flex items-center gap-3 rounded-ctl border border-white/[0.07] bg-white/[0.03] px-4 py-2.5">
                        {cfEditKey === key ? (
                          <>
                            <span className="w-36 shrink-0 text-xs font-bold text-slate-400">{key}</span>
                            <input
                              autoFocus
                              value={cfEditVal}
                              onChange={(e) => setCfEditVal(e.target.value)}
                              onBlur={() => { onSaveCustomField?.(contact.id, key, cfEditVal); setCfEditKey(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { onSaveCustomField?.(contact.id, key, cfEditVal); setCfEditKey(null); } if (e.key === 'Escape') setCfEditKey(null); }}
                              className="flex-1 rounded-ctl border border-cyan-400/40 bg-slate-900 px-3 py-1 text-xs text-white outline-none focus:border-cyan-400"
                            />
                          </>
                        ) : (
                          <>
                            <span className="w-36 shrink-0 text-xs font-bold text-slate-400">{key}</span>
                            <span
                              className="flex-1 cursor-pointer text-xs text-slate-200 hover:text-white"
                              onClick={() => { setCfEditKey(key); setCfEditVal(value); }}
                              title="Click to edit"
                            >{value || <span className="text-slate-600">—</span>}</span>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => onDeleteCustomField?.(contact.id, key)}
                          className="shrink-0 rounded-full p-1 text-slate-600 hover:text-rose-400 transition"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new field */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (cfKey.trim() && cfVal.trim()) {
                      onSaveCustomField?.(contact.id, cfKey.trim(), cfVal.trim());
                      setCfKey('');
                      setCfVal('');
                    }
                  }}
                  className="flex gap-2"
                >
                  <input
                    value={cfKey}
                    onChange={(e) => setCfKey(e.target.value)}
                    placeholder="Field name (e.g. Birthday)"
                    className="flex-1 rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                  />
                  <input
                    value={cfVal}
                    onChange={(e) => setCfVal(e.target.value)}
                    placeholder="Value"
                    className="flex-1 rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                  />
                  <button
                    type="submit"
                    className="flex items-center gap-1.5 rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-micro font-black text-cyan-300 hover:bg-cyan-500/20 transition"
                  >
                    <Plus size={11} /> Add
                  </button>
                </form>
              </div>

              {/* Suggested fields */}
              <div className="rounded-card border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="mb-3 text-micro font-black uppercase tracking-[0.14em] text-slate-600">Suggested fields</div>
                <div className="flex flex-wrap gap-2">
                  {['Birthday', 'Preferred contact time', 'LinkedIn followers', 'Decision authority', 'Budget holder', 'Notice period', 'Preferred channel', 'Anniversary date'].filter(
                    (s) => !customFields.find((f) => f.key === s)
                  ).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setCfKey(s)}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro text-slate-400 hover:border-cyan-400/30 hover:text-cyan-300 transition"
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── CADENCE ── */}
          {tab === 'cadence' && (
            <div className="max-w-2xl space-y-5">
              {cadenceEnrollment ? (
                <>
                  {/* Active enrollment */}
                  <div className="rounded-card border border-violet-400/25 bg-violet-500/8 p-5">
                    <div className="mb-1 text-micro font-black uppercase tracking-[0.2em] text-violet-400">Enrolled</div>
                    <div className="text-lg font-black text-white mb-1">{cadenceEnrollment.cadenceName}</div>
                    <div className="text-xs text-slate-400 mb-4">Step {(cadenceEnrollment.currentStep || 0) + 1} of {totalSteps} · Enrolled {fmtDate(cadenceEnrollment.enrolledAtIso)}</div>

                    {/* Step progress dots */}
                    <div className="flex items-center gap-2 mb-5">
                      {(cadenceTemplates.find((c) => c.id === cadenceEnrollment.cadenceId)?.steps || []).map((step, i) => {
                        const done = i < (cadenceEnrollment.currentStep || 0);
                        const current = i === (cadenceEnrollment.currentStep || 0);
                        return (
                          <React.Fragment key={i}>
                            <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-micro font-black transition ${
                              done ? 'border-violet-400 bg-violet-400 text-slate-950' :
                              current ? 'border-violet-400 bg-violet-500/30 text-violet-300' :
                              'border-white/15 bg-white/5 text-slate-600'
                            }`}>
                              {done ? '✓' : i + 1}
                              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-micro text-slate-600 whitespace-nowrap">{step.channel}</div>
                            </div>
                            {i < totalSteps - 1 && <div className={`h-px flex-1 ${done ? 'bg-violet-400/60' : 'bg-white/10'}`} />}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {activeStep && (
                      <div className="mt-6 rounded-ctl border border-violet-400/15 bg-violet-500/5 p-4">
                        <div className="text-micro font-black uppercase tracking-[0.14em] text-violet-400 mb-2">Current step</div>
                        <div className="text-sm font-bold text-white">{activeStep.label}</div>
                        <div className="mt-1 text-xs text-slate-400">Channel: {activeStep.channel}</div>
                        {cadenceEnrollment.nextStepDueIso && (
                          <div className="mt-1 text-xs text-slate-400">Due: {fmtDate(cadenceEnrollment.nextStepDueIso)}</div>
                        )}
                      </div>
                    )}

                    {/* Step compose form */}
                    {stepCompose && (
                      <div className="mt-4 space-y-3 rounded-card border border-violet-400/25 bg-violet-500/5 p-4">
                        <div className="text-micro font-black uppercase tracking-[0.14em] text-violet-400">
                          {stepCompose.channel === 'email' ? 'Compose email' : stepCompose.channel === 'call' ? 'Call notes' : 'Meeting notes'}
                        </div>
                        {stepCompose.channel === 'email' && (
                          <input
                            value={stepCompose.subject}
                            onChange={(e) => setStepCompose((s) => ({ ...s, subject: e.target.value }))}
                            className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-2.5 text-sm text-white outline-none focus:border-violet-400"
                            placeholder="Subject…"
                          />
                        )}
                        <textarea
                          rows={stepCompose.channel === 'email' ? 10 : 6}
                          value={stepCompose.body}
                          onChange={(e) => setStepCompose((s) => ({ ...s, body: e.target.value }))}
                          className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-200 outline-none focus:border-violet-400 font-mono leading-6"
                        />
                        {/* SMTP status indicator */}
                        {stepCompose.channel === 'email' && (
                          onSendCadenceEmail
                            ? <div className="text-micro text-emerald-400 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" />Will send via SMTP — opens &amp; clicks tracked</div>
                            : <div className="text-micro text-amber-400">Log only — SMTP not configured</div>
                        )}
                        <div className="flex gap-3">
                          <button
                            type="button"
                            disabled={stepLogging}
                            onClick={handleLogAndAdvance}
                            className="flex-1 rounded-full bg-violet-500 py-2.5 text-meta font-black text-white hover:bg-violet-400 disabled:opacity-40 transition"
                          >
                            {stepLogging ? 'Sending…' : stepCompose.channel === 'email' && onSendCadenceEmail ? 'Send & advance →' : `Log ${stepCompose.channel} & advance →`}
                          </button>
                          <button type="button" onClick={() => { onAdvanceCadence?.(cadenceEnrollment.id); setStepCompose(null); }} className="rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-meta font-black text-slate-400 hover:text-white transition">Skip</button>
                          <button type="button" onClick={() => setStepCompose(null)} className="rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-meta text-slate-500 hover:text-white transition">✕</button>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex gap-3">
                      <button
                        type="button"
                        onClick={() => stepCompose ? null : openStepCompose(activeStep)}
                        className="flex-1 rounded-full border border-violet-400/40 bg-violet-500/20 py-2.5 text-meta font-black text-violet-200 hover:bg-violet-500/30 transition"
                      >
                        {stepCompose ? 'Compose open ↑' : 'Complete step → advance'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onPauseCadence?.(cadenceEnrollment.id)}
                        className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-meta font-black text-slate-400 hover:text-white transition"
                      >
                        {cadenceEnrollment.status === 'paused' ? 'Resume' : 'Pause'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-card border border-dashed border-violet-400/20 px-5 py-8 text-center">
                    <Zap size={28} className="mx-auto mb-3 text-violet-700" />
                    <div className="text-sm font-bold text-slate-400">Not enrolled in a cadence.</div>
                    <div className="mt-1 text-xs text-slate-600">Pick a cadence below to start automated follow-up tracking.</div>
                  </div>
                  {cadenceTemplates.map((cad) => (
                    <div key={cad.id} className="rounded-card border border-white/[0.08] bg-slate-900/50 p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="text-sm font-bold text-white">{cad.name}</div>
                          <div className="mt-0.5 text-xs text-slate-500">{cad.steps.length} steps over {cad.steps[cad.steps.length - 1]?.day || 0} days</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => onEnrollCadence?.(contact.id, cad.id)}
                          className="shrink-0 rounded-full border border-violet-400/30 bg-violet-500/10 px-4 py-2 text-micro font-black text-violet-300 hover:bg-violet-500/20 transition"
                        >
                          Enroll
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {cad.steps.map((step, i) => (
                          <React.Fragment key={i}>
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-violet-400/25 bg-violet-500/10 text-micro font-black text-violet-400">{i + 1}</div>
                              <div className="text-micro text-slate-600">{step.channel}</div>
                              <div className="text-micro text-slate-700">d{step.day}</div>
                            </div>
                            {i < cad.steps.length - 1 && <div className="h-px flex-1 bg-white/5" />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── LINKED RECORDS ── */}
          {tab === 'linked' && (
            <div className="max-w-2xl space-y-5">
              {/* Opportunities */}
              {opportunities.length > 0 && (
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Linked opportunities ({opportunities.length})</div>
                  <div className="space-y-2">
                    {opportunities.map((opp) => (
                      <div key={opp.id} className="flex items-center gap-3 rounded-ctl border border-white/[0.06] bg-white/[0.025] px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-white truncate">{opp.title}</div>
                          <div className="text-xs text-slate-500">{opp.stage} · {opp.companyName}</div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-xs font-black text-cyan-300">{opp.fee ? Number(opp.fee).toLocaleString() : '—'}</div>
                          <div className="text-micro text-slate-600">{opp.probability ? `${Math.round((opp.probability || 0) * 100)}% prob` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active job orders */}
              {jobs.length > 0 && (
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Active job orders ({jobs.length})</div>
                  <div className="space-y-2">
                    {jobs.map((job) => (
                      <div key={job.id} className="flex items-center gap-3 rounded-ctl border border-white/[0.06] bg-white/[0.025] px-4 py-3">
                        <Briefcase size={12} className="shrink-0 text-slate-500" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-white truncate">{job.title}</div>
                          {job.location && <div className="text-xs text-slate-500">{job.location}</div>}
                        </div>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-micro font-black ${(job.status || '').toLowerCase() === 'active' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/5 text-slate-500'}`}>
                          {job.status || 'Active'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {opportunities.length === 0 && jobs.length === 0 && (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Briefcase size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No linked records found.</div>
                  <div className="mt-1 text-xs text-slate-600">Opportunities and job orders linked to this contact's company will appear here.</div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
